/**
 * Debug Overlay for DirectCanvas
 *
 * Provides visual debugging tools for the canvas:
 * - Layer visibility toggles (HTML, SVG, Grid)
 * - LOD visualization (color-coded by level)
 * - Spatial index visualization
 * - Frustum bounds visualization
 * - Performance metrics (FPS, node count, visible count)
 *
 * The debug overlay is rendered as an HTML panel and optional
 * canvas overlays for visualizations.
 */

import type { Camera, ViewportSize, WorldRect, UINode } from './types';

/**
 * Debug options that can be toggled.
 */
export interface DebugOptions {
	/** Show the debug panel */
	showPanel: boolean;

	/** Layer visibility toggles */
	showGridLayer: boolean;
	showSVGLayer: boolean;
	showHTMLLayer: boolean;
	showTextureLayer: boolean;

	/** Visual debugging */
	showLODColors: boolean;
	showSpatialIndex: boolean;
	showFrustumBounds: boolean;
	showNodeBounds: boolean;
	/** Highlight nodes to show texture (purple) vs HTML (green) rendering */
	showRenderMode: boolean;
	/** Show texture queue state (pending=yellow, processing=orange, skipped=red) */
	showTextureQueue: boolean;

	/** Performance */
	showPerformanceMetrics: boolean;

	/** Rendering behavior */
	/** Whether to simplify LOD when zooming out (default: true). When false, always use 'full' LOD. */
	simplifyLOD: boolean;
	/** Force texture rendering at distance without changing LOD content */
	forceTextureAtDistance: boolean;

	/** LOD and texture thresholds (in screen pixels) */
	/** Threshold below which nodes render as texture (default 60, or 150 when forceTextureAtDistance) */
	textureThreshold: number;
	/** Threshold for full LOD (default 150) */
	lodFullThreshold: number;
	/** Threshold for title LOD (default 60) */
	lodTitleThreshold: number;
	/** Threshold for icon LOD (default 20) */
	lodIconThreshold: number;

	/** LOD level toggles - allows disabling specific LOD levels for testing */
	/** Enable full LOD level (default: true) */
	enableLodFull: boolean;
	/** Enable title LOD level (default: true) */
	enableLodTitle: boolean;
	/** Enable icon LOD level (default: true) */
	enableLodIcon: boolean;
	/** Enable dot LOD level (default: true) */
	enableLodDot: boolean;

	/** Texture generation settings */
	/** Texture resolution multiplier (default: 1.0) */
	textureResolutionMultiplier: number;
	/** Default target size for textures in pixels (default: 150) */
	textureDefaultTargetSize: number;

	/** Force a specific LOD level (overrides zoom-based calculation) */
	forceLod: 'full' | 'title' | 'icon' | 'dot' | null;
	/** Force all nodes to use WebGL texture rendering instead of HTML */
	forceTextureRendering: boolean;
}

/**
 * Performance metrics collected by the debug overlay.
 */
export interface PerformanceMetrics {
	fps: number;
	frameTime: number;
	totalNodes: number;
	visibleNodes: number;
	selectedNodes: number;
	totalEdges: number;
	domElements: number;
	spatialIndexSize: number;
	// Phase 0.7: Texture cache stats
	textureCacheCount: number;
	textureCacheMemoryMB: number;
	textureCacheHits: number;
	textureCacheMisses: number;
	texturePendingRequests: number;
	// Phase 0.7: Entity rendering counts
	htmlNodesRendered: number;
	textureNodesRendered: number;
	svgEdgesRendered: number;
	// Phase 0.7: Texture queue debug stats
	textureQueueLength: number;
	texturesGenerated: number;
	texturesSkipped: number;
	currentlyProcessing: string | null;
	recentSkipReasons: Record<string, number>;
}

/**
 * LOD colors for visualization.
 */
const LOD_COLORS: Record<string, string> = {
	full: 'rgba(34, 197, 94, 0.3)', // green
	title: 'rgba(59, 130, 246, 0.3)', // blue
	icon: 'rgba(249, 115, 22, 0.3)', // orange
	dot: 'rgba(239, 68, 68, 0.3)' // red
};

/**
 * Default debug options.
 */
const DEFAULT_OPTIONS: DebugOptions = {
	showPanel: false,
	showGridLayer: true,
	showSVGLayer: true,
	showHTMLLayer: true,
	showTextureLayer: true,
	showLODColors: false,
	showSpatialIndex: false,
	showFrustumBounds: false,
	showNodeBounds: false,
	showRenderMode: false,
	showTextureQueue: false,
	showPerformanceMetrics: true,
	simplifyLOD: true,
	forceTextureAtDistance: false,
	textureThreshold: 60,
	lodFullThreshold: 150,
	lodTitleThreshold: 60,
	lodIconThreshold: 20,
	enableLodFull: true,
	enableLodTitle: true,
	enableLodIcon: true,
	enableLodDot: true,
	textureResolutionMultiplier: 1.0,
	textureDefaultTargetSize: 150,
	forceLod: null,
	forceTextureRendering: false
};

/**
 * Debug Overlay class.
 * Attaches to a canvas container and provides visual debugging tools.
 */
export class DebugOverlay {
	private container: HTMLElement;
	private panel: HTMLElement | null = null;
	private overlayCanvas: HTMLCanvasElement | null = null;
	private overlayCtx: CanvasRenderingContext2D | null = null;

	private options: DebugOptions = { ...DEFAULT_OPTIONS };
	private metrics: PerformanceMetrics = {
		fps: 0,
		frameTime: 0,
		totalNodes: 0,
		visibleNodes: 0,
		selectedNodes: 0,
		totalEdges: 0,
		domElements: 0,
		spatialIndexSize: 0,
		// Phase 0.7: Texture cache stats
		textureCacheCount: 0,
		textureCacheMemoryMB: 0,
		textureCacheHits: 0,
		textureCacheMisses: 0,
		texturePendingRequests: 0,
		// Phase 0.7: Entity rendering counts
		htmlNodesRendered: 0,
		textureNodesRendered: 0,
		svgEdgesRendered: 0,
		// Phase 0.7: Texture queue debug stats
		textureQueueLength: 0,
		texturesGenerated: 0,
		texturesSkipped: 0,
		currentlyProcessing: null,
		recentSkipReasons: {}
	};

	// FPS tracking
	private frameCount = 0;
	private lastFpsUpdate = 0;
	private frameTimes: number[] = [];
	private lastFrameTime = 0;

	// Callbacks for layer visibility changes
	private onLayerVisibilityChange: ((layer: string, visible: boolean) => void) | null = null;

	// Callback for option changes (for options that are canvas settings, not debug-only)
	private onOptionChange: ((option: string, value: unknown) => void) | null = null;

	// Callback for getting texture previews from canvas
	private getTexturePreviews: (() => Array<{ nodeId: string; width: number; height: number; memoryBytes: number; dataUrl: string }>) | null = null;

	// References to canvas elements
	private gridLayer: HTMLElement | null = null;
	private webglGridLayer: HTMLElement | null = null;
	private svgLayer: HTMLElement | null = null;
	private htmlLayer: HTMLElement | null = null;
	private textureLayer: HTMLElement | null = null;

	// Spatial index data for visualization
	private spatialIndexBounds: WorldRect[] = [];

	// Camera and viewport for coordinate transforms
	private camera: Camera = { x: 0, y: 0, zoom: 1 };
	private viewport: ViewportSize = { width: 0, height: 0 };

	// Visible nodes for LOD visualization
	private visibleNodes: UINode[] = [];

	constructor(container: HTMLElement) {
		this.container = container;
	}

	/**
	 * Initialize the debug overlay.
	 * Call after the canvas is set up.
	 */
	initialize(
		gridLayer: HTMLElement | null,
		svgContainer: HTMLElement | null,
		htmlContainer: HTMLElement | null,
		webglGridLayer?: HTMLElement | null,
		textureLayer?: HTMLElement | null
	): void {
		this.gridLayer = gridLayer;
		this.webglGridLayer = webglGridLayer ?? null;
		this.svgLayer = svgContainer;
		this.htmlLayer = htmlContainer;
		this.textureLayer = textureLayer ?? null;

		if (this.options.showPanel) {
			this.createPanel();
		}
	}

	/**
	 * Set the callback for layer visibility changes.
	 */
	setOnLayerVisibilityChange(callback: (layer: string, visible: boolean) => void): void {
		this.onLayerVisibilityChange = callback;
	}

	/**
	 * Set the callback for option changes.
	 * Used for options like simplifyLOD that are canvas settings exposed in debug panel.
	 */
	setOnOptionChange(callback: (option: string, value: unknown) => void): void {
		this.onOptionChange = callback;
	}

	/**
	 * Set the callback for getting texture previews.
	 * Used to display cached textures in the debug panel.
	 */
	setGetTexturePreviews(callback: () => Array<{ nodeId: string; width: number; height: number; memoryBytes: number; dataUrl: string }>): void {
		this.getTexturePreviews = callback;
	}

	/**
	 * Get current debug options.
	 */
	getOptions(): DebugOptions {
		return { ...this.options };
	}

	/**
	 * Check if the debug panel is visible.
	 * Used to skip expensive debug calculations when panel is hidden.
	 */
	isVisible(): boolean {
		return this.options.showPanel;
	}

	/**
	 * Set debug options.
	 */
	setOptions(options: Partial<DebugOptions>): void {
		const prevShowPanel = this.options.showPanel;
		this.options = { ...this.options, ...options };

		// Handle panel visibility
		if (options.showPanel !== undefined && options.showPanel !== prevShowPanel) {
			if (options.showPanel) {
				this.createPanel();
			} else {
				this.destroyPanel();
			}
		}

		// Apply layer visibility
		this.applyLayerVisibility();

		// Notify about option changes (for options that are canvas settings)
		if (this.onOptionChange) {
			if (options.simplifyLOD !== undefined) {
				this.onOptionChange('simplifyLOD', options.simplifyLOD);
			}
			if (options.forceLod !== undefined) {
				this.onOptionChange('forceLod', options.forceLod);
			}
			if (options.forceTextureRendering !== undefined) {
				this.onOptionChange('forceTextureRendering', options.forceTextureRendering);
			}
		}

		// Update panel if it exists
		this.updatePanel();
	}

	/**
	 * Toggle the debug panel visibility.
	 */
	toggle(): void {
		this.setOptions({ showPanel: !this.options.showPanel });
	}

	/**
	 * Update camera and viewport for coordinate transforms.
	 */
	setCamera(camera: Camera, viewport: ViewportSize): void {
		this.camera = camera;
		this.viewport = viewport;

		// Set CSS variable for zoom level (used by debug render mode borders)
		this.container.style.setProperty('--debug-zoom', String(camera.zoom));
	}

	/**
	 * Update visible nodes for LOD visualization.
	 */
	setVisibleNodes(nodes: UINode[]): void {
		this.visibleNodes = nodes;
	}

	/**
	 * Update spatial index bounds for visualization.
	 */
	setSpatialIndexBounds(bounds: WorldRect[]): void {
		this.spatialIndexBounds = bounds;
	}

	/**
	 * Update performance metrics.
	 */
	updateMetrics(metrics: Partial<PerformanceMetrics>): void {
		this.metrics = { ...this.metrics, ...metrics };
		this.updatePanel();
	}

	/**
	 * Track a frame for FPS calculation.
	 * Call this once per frame.
	 */
	trackFrame(): void {
		const now = performance.now();

		// Track frame time
		if (this.lastFrameTime > 0) {
			const frameTime = now - this.lastFrameTime;
			this.frameTimes.push(frameTime);
			if (this.frameTimes.length > 60) {
				this.frameTimes.shift();
			}
			this.metrics.frameTime = frameTime;
		}
		this.lastFrameTime = now;

		// Update FPS every second
		this.frameCount++;
		if (now - this.lastFpsUpdate >= 1000) {
			this.metrics.fps = Math.round((this.frameCount * 1000) / (now - this.lastFpsUpdate));
			this.frameCount = 0;
			this.lastFpsUpdate = now;
			this.updatePanel();
		}
	}

	/**
	 * Render debug overlays.
	 * Call this after the main canvas render.
	 */
	render(): void {
		if (!this.options.showPanel) return;

		// Create overlay canvas if needed
		if (!this.overlayCanvas && (this.options.showSpatialIndex || this.options.showFrustumBounds)) {
			this.createOverlayCanvas();
		}

		if (this.overlayCanvas && this.overlayCtx) {
			this.overlayCtx.clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);

			if (this.options.showFrustumBounds) {
				this.renderFrustumBounds();
			}

			if (this.options.showSpatialIndex) {
				this.renderSpatialIndex();
			}
		}

		// Apply LOD colors to nodes
		if (this.options.showLODColors) {
			this.applyLODColors();
		} else {
			this.clearLODColors();
		}

		// Apply node bounds visualization
		if (this.options.showNodeBounds) {
			this.applyNodeBounds();
		} else {
			this.clearNodeBounds();
		}
	}

	/**
	 * Clean up resources.
	 */
	destroy(): void {
		this.destroyPanel();
		this.destroyOverlayCanvas();
		this.clearLODColors();
		this.clearNodeBounds();
	}

	// =========================================================================
	// Panel Management
	// =========================================================================

	private createPanel(): void {
		if (this.panel) return;

		this.panel = document.createElement('div');
		this.panel.className = 'debug-overlay-panel';
		this.panel.innerHTML = this.getPanelHTML();
		this.container.appendChild(this.panel);

		this.attachPanelEvents();
		this.applyLayerVisibility();
	}

	private destroyPanel(): void {
		if (this.panel) {
			this.panel.remove();
			this.panel = null;
		}
		this.destroyOverlayCanvas();
	}

	private getPanelHTML(): string {
		return `
			<div class="debug-panel-header">
				<span class="debug-panel-title">Debug</span>
				<button class="debug-panel-close" data-action="close">&times;</button>
			</div>
			<div class="debug-panel-content">
				<div class="debug-section debug-entity-summary">
					<div class="debug-section-title">Rendering</div>
					<div class="debug-entity-bars">
						<div class="debug-entity-bar">
							<span class="debug-entity-label">HTML</span>
							<span class="debug-entity-count debug-entity-html" id="debug-html-count">${this.metrics.htmlNodesRendered}</span>
							<div class="debug-entity-bar-fill debug-entity-html-bar" id="debug-html-bar"></div>
						</div>
						<div class="debug-entity-bar">
							<span class="debug-entity-label">WebGL</span>
							<span class="debug-entity-count debug-entity-texture" id="debug-texture-count-vis">${this.metrics.textureNodesRendered}</span>
							<div class="debug-entity-bar-fill debug-entity-texture-bar" id="debug-texture-bar"></div>
						</div>
						<div class="debug-entity-bar">
							<span class="debug-entity-label">SVG</span>
							<span class="debug-entity-count debug-entity-svg" id="debug-svg-count">${this.metrics.svgEdgesRendered}</span>
							<div class="debug-entity-bar-fill debug-entity-svg-bar" id="debug-svg-bar"></div>
						</div>
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Layers</div>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showGridLayer" ${this.options.showGridLayer ? 'checked' : ''}>
						Grid Layer
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showSVGLayer" ${this.options.showSVGLayer ? 'checked' : ''}>
						SVG Layer (Edges)
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showTextureLayer" ${this.options.showTextureLayer ? 'checked' : ''}>
						Texture Layer (WebGL)
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showHTMLLayer" ${this.options.showHTMLLayer ? 'checked' : ''}>
						HTML Layer (Nodes)
					</label>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Visualization</div>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showLODColors" ${this.options.showLODColors ? 'checked' : ''}>
						LOD Colors
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showRenderMode" ${this.options.showRenderMode ? 'checked' : ''}>
						Render Mode (Texture=purple, HTML=green)
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showTextureQueue" ${this.options.showTextureQueue ? 'checked' : ''}>
						Texture Queue (pending/processing/skipped)
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showNodeBounds" ${this.options.showNodeBounds ? 'checked' : ''}>
						Node Bounds
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showFrustumBounds" ${this.options.showFrustumBounds ? 'checked' : ''}>
						Frustum Bounds
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="showSpatialIndex" ${this.options.showSpatialIndex ? 'checked' : ''}>
						Spatial Index
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="simplifyLOD" ${this.options.simplifyLOD ? 'checked' : ''}>
						Simplify LOD (zoom out)
					</label>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="forceTextureAtDistance" ${this.options.forceTextureAtDistance ? 'checked' : ''}>
						Force Texture (no LOD change)
					</label>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">LOD Levels</div>
					<div class="debug-lod-toggles">
						<label class="debug-lod-toggle">
							<input type="checkbox" data-option="enableLodFull" ${this.options.enableLodFull ? 'checked' : ''}>
							<span class="debug-lod-toggle-label" style="--lod-color: ${LOD_COLORS.full}">Full</span>
						</label>
						<label class="debug-lod-toggle">
							<input type="checkbox" data-option="enableLodTitle" ${this.options.enableLodTitle ? 'checked' : ''}>
							<span class="debug-lod-toggle-label" style="--lod-color: ${LOD_COLORS.title}">Title</span>
						</label>
						<label class="debug-lod-toggle">
							<input type="checkbox" data-option="enableLodIcon" ${this.options.enableLodIcon ? 'checked' : ''}>
							<span class="debug-lod-toggle-label" style="--lod-color: ${LOD_COLORS.icon}">Icon</span>
						</label>
						<label class="debug-lod-toggle">
							<input type="checkbox" data-option="enableLodDot" ${this.options.enableLodDot ? 'checked' : ''}>
							<span class="debug-lod-toggle-label" style="--lod-color: ${LOD_COLORS.dot}">Dot</span>
						</label>
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Force LOD/Texture (Testing)</div>
					<div class="debug-force-lod">
						<label class="debug-radio">
							<input type="radio" name="forceLod" data-force-lod="null" ${!this.options.forceLod ? 'checked' : ''}>
							Auto
						</label>
						<label class="debug-radio">
							<input type="radio" name="forceLod" data-force-lod="full" ${this.options.forceLod === 'full' ? 'checked' : ''}>
							Full
						</label>
						<label class="debug-radio">
							<input type="radio" name="forceLod" data-force-lod="title" ${this.options.forceLod === 'title' ? 'checked' : ''}>
							Title
						</label>
						<label class="debug-radio">
							<input type="radio" name="forceLod" data-force-lod="icon" ${this.options.forceLod === 'icon' ? 'checked' : ''}>
							Icon
						</label>
						<label class="debug-radio">
							<input type="radio" name="forceLod" data-force-lod="dot" ${this.options.forceLod === 'dot' ? 'checked' : ''}>
							Dot
						</label>
					</div>
					<label class="debug-checkbox">
						<input type="checkbox" data-option="forceTextureRendering" ${this.options.forceTextureRendering ? 'checked' : ''}>
						Force WebGL Textures (no HTML)
					</label>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Thresholds (px)</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">Texture</label>
						<input type="number" class="debug-threshold-input" data-threshold="textureThreshold" value="${this.options.textureThreshold}" min="10" max="500" step="10">
					</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">LOD Full</label>
						<input type="number" class="debug-threshold-input" data-threshold="lodFullThreshold" value="${this.options.lodFullThreshold}" min="10" max="500" step="10">
					</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">LOD Title</label>
						<input type="number" class="debug-threshold-input" data-threshold="lodTitleThreshold" value="${this.options.lodTitleThreshold}" min="10" max="500" step="10">
					</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">LOD Icon</label>
						<input type="number" class="debug-threshold-input" data-threshold="lodIconThreshold" value="${this.options.lodIconThreshold}" min="5" max="200" step="5">
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Texture Generation</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">Resolution Mult</label>
						<input type="number" class="debug-threshold-input" data-texture-option="textureResolutionMultiplier" value="${this.options.textureResolutionMultiplier}" min="0.1" max="4" step="0.1">
					</div>
					<div class="debug-threshold">
						<label class="debug-threshold-label">Target Size</label>
						<input type="number" class="debug-threshold-input" data-texture-option="textureDefaultTargetSize" value="${this.options.textureDefaultTargetSize}" min="32" max="512" step="16">
					</div>
					<div class="debug-hint" style="font-size: 9px; color: #666; margin-top: 4px; padding: 0 4px;">
						Final texture size = Target Size × Resolution Mult
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Performance</div>
					<div class="debug-metrics">
						<div class="debug-metric">
							<span class="debug-metric-label">FPS</span>
							<span class="debug-metric-value" id="debug-fps">${this.metrics.fps}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Frame Time</span>
							<span class="debug-metric-value" id="debug-frametime">${this.metrics.frameTime.toFixed(1)}ms</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Total Nodes</span>
							<span class="debug-metric-value" id="debug-total-nodes">${this.metrics.totalNodes}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Visible Nodes</span>
							<span class="debug-metric-value" id="debug-visible-nodes">${this.metrics.visibleNodes}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Selected</span>
							<span class="debug-metric-value" id="debug-selected-nodes">${this.metrics.selectedNodes}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Total Edges</span>
							<span class="debug-metric-value" id="debug-total-edges">${this.metrics.totalEdges}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">DOM Elements</span>
							<span class="debug-metric-value" id="debug-dom-elements">${this.metrics.domElements}</span>
						</div>
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">Texture Cache (Phase 0.7)</div>
					<div class="debug-metrics">
						<div class="debug-metric">
							<span class="debug-metric-label">Cached Textures</span>
							<span class="debug-metric-value" id="debug-texture-count">${this.metrics.textureCacheCount}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Memory</span>
							<span class="debug-metric-value" id="debug-texture-memory">${this.metrics.textureCacheMemoryMB.toFixed(1)} MB</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Cache Hits</span>
							<span class="debug-metric-value" id="debug-texture-hits">${this.metrics.textureCacheHits}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Cache Misses</span>
							<span class="debug-metric-value" id="debug-texture-misses">${this.metrics.textureCacheMisses}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Pending</span>
							<span class="debug-metric-value" id="debug-texture-pending">${this.metrics.texturePendingRequests}</span>
						</div>
					</div>
					<div class="debug-texture-preview-header">
						<span class="debug-section-subtitle">Texture Preview</span>
						<button class="debug-refresh-btn" data-action="refresh-textures">↻</button>
					</div>
					<div class="debug-texture-previews" id="debug-texture-previews">
						<div class="debug-texture-empty">Click ↻ to load previews</div>
					</div>
				</div>
				<div class="debug-section debug-texture-queue-section">
					<div class="debug-section-title">Texture Queue Debug</div>
					<div class="debug-metrics">
						<div class="debug-metric">
							<span class="debug-metric-label">Queue Length</span>
							<span class="debug-metric-value" id="debug-queue-length">${this.metrics.textureQueueLength}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Generated</span>
							<span class="debug-metric-value debug-entity-html" id="debug-textures-generated">${this.metrics.texturesGenerated}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Skipped</span>
							<span class="debug-metric-value" id="debug-textures-skipped" style="color: #ef4444">${this.metrics.texturesSkipped}</span>
						</div>
						<div class="debug-metric">
							<span class="debug-metric-label">Processing</span>
							<span class="debug-metric-value" id="debug-currently-processing" style="color: #f97316">${this.metrics.currentlyProcessing || '—'}</span>
						</div>
					</div>
					<div class="debug-skip-reasons" id="debug-skip-reasons"></div>
					<div class="debug-queue-legend">
						<span class="debug-queue-item debug-queue-pending">Pending</span>
						<span class="debug-queue-item debug-queue-processing">Processing</span>
						<span class="debug-queue-item debug-queue-skipped">Skipped</span>
						<span class="debug-queue-item debug-queue-cached">Cached</span>
					</div>
				</div>
				<div class="debug-section">
					<div class="debug-section-title">LOD Legend</div>
					<div class="debug-lod-legend">
						<span class="debug-lod-item" style="--lod-color: ${LOD_COLORS.full}">Full</span>
						<span class="debug-lod-item" style="--lod-color: ${LOD_COLORS.title}">Title</span>
						<span class="debug-lod-item" style="--lod-color: ${LOD_COLORS.icon}">Icon</span>
						<span class="debug-lod-item" style="--lod-color: ${LOD_COLORS.dot}">Dot</span>
					</div>
				</div>
			</div>
		`;
	}

	private attachPanelEvents(): void {
		if (!this.panel) return;

		// Close button
		this.panel.querySelector('[data-action="close"]')?.addEventListener('click', () => {
			this.setOptions({ showPanel: false });
		});

		// Refresh textures button
		this.panel.querySelector('[data-action="refresh-textures"]')?.addEventListener('click', () => {
			this.refreshTexturePreviews();
		});

		// Checkboxes
		this.panel.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
			checkbox.addEventListener('change', (e) => {
				const target = e.target as HTMLInputElement;
				const option = target.dataset.option as keyof DebugOptions;
				if (option) {
					this.setOptions({ [option]: target.checked } as Partial<DebugOptions>);
				}
			});
		});

		// Force LOD radio buttons
		this.panel.querySelectorAll('input[data-force-lod]').forEach((radio) => {
			radio.addEventListener('change', (e) => {
				const target = e.target as HTMLInputElement;
				const value = target.dataset.forceLod;
				const forceLod = value === 'null' ? null : (value as 'full' | 'title' | 'icon' | 'dot');
				this.setOptions({ forceLod });
			});
		});

		// Threshold inputs
		this.panel.querySelectorAll('input[data-threshold]').forEach((input) => {
			input.addEventListener('change', (e) => {
				const target = e.target as HTMLInputElement;
				const threshold = target.dataset.threshold as keyof DebugOptions;
				if (threshold) {
					const value = parseInt(target.value, 10);
					if (!isNaN(value) && value > 0) {
						this.setOptions({ [threshold]: value } as Partial<DebugOptions>);
					}
				}
			});
		});

		// Texture generation option inputs
		this.panel.querySelectorAll('input[data-texture-option]').forEach((input) => {
			input.addEventListener('change', (e) => {
				const target = e.target as HTMLInputElement;
				const option = target.dataset.textureOption as keyof DebugOptions;
				if (option) {
					const value = parseFloat(target.value);
					if (!isNaN(value) && value > 0) {
						this.setOptions({ [option]: value } as Partial<DebugOptions>);
						// Notify callback for texture options that affect canvas directly
						if (this.onOptionChange) {
							this.onOptionChange(option, value);
						}
					}
				}
			});
		});
	}

	private updatePanel(): void {
		if (!this.panel) return;

		// Update metrics
		const fpsEl = this.panel.querySelector('#debug-fps');
		const frametimeEl = this.panel.querySelector('#debug-frametime');
		const totalNodesEl = this.panel.querySelector('#debug-total-nodes');
		const visibleNodesEl = this.panel.querySelector('#debug-visible-nodes');
		const selectedNodesEl = this.panel.querySelector('#debug-selected-nodes');
		const totalEdgesEl = this.panel.querySelector('#debug-total-edges');
		const domElementsEl = this.panel.querySelector('#debug-dom-elements');

		if (fpsEl) {
			fpsEl.textContent = String(this.metrics.fps);
			fpsEl.className =
				'debug-metric-value' +
				(this.metrics.fps < 30 ? ' critical' : this.metrics.fps < 55 ? ' warning' : '');
		}
		if (frametimeEl) frametimeEl.textContent = `${this.metrics.frameTime.toFixed(1)}ms`;
		if (totalNodesEl) totalNodesEl.textContent = String(this.metrics.totalNodes);
		if (visibleNodesEl) visibleNodesEl.textContent = String(this.metrics.visibleNodes);
		if (selectedNodesEl) selectedNodesEl.textContent = String(this.metrics.selectedNodes);
		if (totalEdgesEl) totalEdgesEl.textContent = String(this.metrics.totalEdges);
		if (domElementsEl) domElementsEl.textContent = String(this.metrics.domElements);

		// Update texture cache stats (Phase 0.7)
		const textureCountEl = this.panel.querySelector('#debug-texture-count');
		const textureMemoryEl = this.panel.querySelector('#debug-texture-memory');
		const textureHitsEl = this.panel.querySelector('#debug-texture-hits');
		const textureMissesEl = this.panel.querySelector('#debug-texture-misses');
		const texturePendingEl = this.panel.querySelector('#debug-texture-pending');

		if (textureCountEl) textureCountEl.textContent = String(this.metrics.textureCacheCount);
		if (textureMemoryEl) textureMemoryEl.textContent = `${this.metrics.textureCacheMemoryMB.toFixed(1)} MB`;
		if (textureHitsEl) textureHitsEl.textContent = String(this.metrics.textureCacheHits);
		if (textureMissesEl) textureMissesEl.textContent = String(this.metrics.textureCacheMisses);
		if (texturePendingEl) texturePendingEl.textContent = String(this.metrics.texturePendingRequests);

		// Update texture queue debug stats
		const queueLengthEl = this.panel.querySelector('#debug-queue-length');
		const texturesGeneratedEl = this.panel.querySelector('#debug-textures-generated');
		const texturesSkippedEl = this.panel.querySelector('#debug-textures-skipped');
		const currentlyProcessingEl = this.panel.querySelector('#debug-currently-processing');
		const skipReasonsEl = this.panel.querySelector('#debug-skip-reasons');

		if (queueLengthEl) {
			queueLengthEl.textContent = String(this.metrics.textureQueueLength);
			queueLengthEl.className = 'debug-metric-value' + (this.metrics.textureQueueLength > 100 ? ' warning' : '');
		}
		if (texturesGeneratedEl) texturesGeneratedEl.textContent = String(this.metrics.texturesGenerated);
		if (texturesSkippedEl) texturesSkippedEl.textContent = String(this.metrics.texturesSkipped);
		if (currentlyProcessingEl) {
			const processing = this.metrics.currentlyProcessing;
			currentlyProcessingEl.textContent = processing ? processing.slice(0, 20) + (processing.length > 20 ? '...' : '') : '—';
		}
		if (skipReasonsEl && this.metrics.recentSkipReasons) {
			const reasons = Object.entries(this.metrics.recentSkipReasons);
			if (reasons.length > 0) {
				skipReasonsEl.innerHTML = reasons
					.map(([reason, count]) => `<span class="debug-skip-reason">${reason}: ${count}</span>`)
					.join('');
			} else {
				skipReasonsEl.innerHTML = '';
			}
		}

		// Update entity rendering counts
		const htmlCountEl = this.panel.querySelector('#debug-html-count');
		const textureCountVisEl = this.panel.querySelector('#debug-texture-count-vis');
		const svgCountEl = this.panel.querySelector('#debug-svg-count');
		const htmlBarEl = this.panel.querySelector('#debug-html-bar') as HTMLElement;
		const textureBarEl = this.panel.querySelector('#debug-texture-bar') as HTMLElement;
		const svgBarEl = this.panel.querySelector('#debug-svg-bar') as HTMLElement;

		if (htmlCountEl) htmlCountEl.textContent = String(this.metrics.htmlNodesRendered);
		if (textureCountVisEl) textureCountVisEl.textContent = String(this.metrics.textureNodesRendered);
		if (svgCountEl) svgCountEl.textContent = String(this.metrics.svgEdgesRendered);

		// Update bar widths (relative to max of all counts)
		const maxCount = Math.max(
			this.metrics.htmlNodesRendered,
			this.metrics.textureNodesRendered,
			this.metrics.svgEdgesRendered,
			1 // Prevent division by zero
		);
		if (htmlBarEl) htmlBarEl.style.width = `${(this.metrics.htmlNodesRendered / maxCount) * 100}%`;
		if (textureBarEl) textureBarEl.style.width = `${(this.metrics.textureNodesRendered / maxCount) * 100}%`;
		if (svgBarEl) svgBarEl.style.width = `${(this.metrics.svgEdgesRendered / maxCount) * 100}%`;

		// Update checkboxes
		this.panel.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
			const input = checkbox as HTMLInputElement;
			const option = input.dataset.option as keyof DebugOptions;
			if (option && typeof this.options[option] === 'boolean') {
				input.checked = this.options[option] as boolean;
			}
		});
	}

	// =========================================================================
	// Layer Visibility
	// =========================================================================

	private applyLayerVisibility(): void {
		// Handle both CSS grid and WebGL grid
		if (this.gridLayer) {
			this.gridLayer.style.display = this.options.showGridLayer ? '' : 'none';
		}
		if (this.webglGridLayer) {
			this.webglGridLayer.style.display = this.options.showGridLayer ? '' : 'none';
		}
		if (this.svgLayer) {
			this.svgLayer.style.display = this.options.showSVGLayer ? '' : 'none';
		}
		if (this.textureLayer) {
			this.textureLayer.style.display = this.options.showTextureLayer ? '' : 'none';
		}
		if (this.htmlLayer) {
			this.htmlLayer.style.display = this.options.showHTMLLayer ? '' : 'none';
		}

		// Apply render mode visualization class to container
		this.container.classList.toggle('debug-render-mode', this.options.showRenderMode);

		// Apply texture queue debug class to container
		this.container.classList.toggle('debug-texture-queue', this.options.showTextureQueue);

		// Notify callback
		if (this.onLayerVisibilityChange) {
			this.onLayerVisibilityChange('grid', this.options.showGridLayer);
			this.onLayerVisibilityChange('svg', this.options.showSVGLayer);
			this.onLayerVisibilityChange('texture', this.options.showTextureLayer);
			this.onLayerVisibilityChange('html', this.options.showHTMLLayer);
		}
	}

	// =========================================================================
	// Overlay Canvas
	// =========================================================================

	private createOverlayCanvas(): void {
		if (this.overlayCanvas) return;

		this.overlayCanvas = document.createElement('canvas');
		this.overlayCanvas.className = 'debug-overlay-canvas';
		this.overlayCanvas.style.position = 'absolute';
		this.overlayCanvas.style.top = '0';
		this.overlayCanvas.style.left = '0';
		this.overlayCanvas.style.width = '100%';
		this.overlayCanvas.style.height = '100%';
		this.overlayCanvas.style.pointerEvents = 'none';
		this.overlayCanvas.style.zIndex = '9999';

		// Set canvas size
		const rect = this.container.getBoundingClientRect();
		const dpr = Math.min(window.devicePixelRatio || 1, 2);
		this.overlayCanvas.width = rect.width * dpr;
		this.overlayCanvas.height = rect.height * dpr;

		this.overlayCtx = this.overlayCanvas.getContext('2d');
		if (this.overlayCtx) {
			this.overlayCtx.scale(dpr, dpr);
		}

		this.container.appendChild(this.overlayCanvas);
	}

	private destroyOverlayCanvas(): void {
		if (this.overlayCanvas) {
			this.overlayCanvas.remove();
			this.overlayCanvas = null;
			this.overlayCtx = null;
		}
	}

	// =========================================================================
	// Visualizations
	// =========================================================================

	private renderFrustumBounds(): void {
		if (!this.overlayCtx) return;

		const ctx = this.overlayCtx;
		const { width, height } = this.viewport;

		// Draw frustum rectangle (the entire viewport)
		ctx.strokeStyle = 'rgba(255, 0, 255, 0.8)';
		ctx.lineWidth = 2;
		ctx.setLineDash([10, 5]);
		ctx.strokeRect(10, 10, width - 20, height - 20);
		ctx.setLineDash([]);

		// Draw center crosshair
		ctx.strokeStyle = 'rgba(255, 0, 255, 0.5)';
		ctx.lineWidth = 1;
		ctx.beginPath();
		ctx.moveTo(width / 2 - 20, height / 2);
		ctx.lineTo(width / 2 + 20, height / 2);
		ctx.moveTo(width / 2, height / 2 - 20);
		ctx.lineTo(width / 2, height / 2 + 20);
		ctx.stroke();

		// Label
		ctx.fillStyle = 'rgba(255, 0, 255, 0.8)';
		ctx.font = '12px monospace';
		ctx.fillText(`Camera: (${this.camera.x.toFixed(0)}, ${this.camera.y.toFixed(0)})`, 15, 25);
		ctx.fillText(`Zoom: ${this.camera.zoom.toFixed(2)}`, 15, 40);
	}

	private renderSpatialIndex(): void {
		if (!this.overlayCtx) return;

		const ctx = this.overlayCtx;

		// Scale line width so it's visible at all zoom levels
		const lineWidth = Math.max(1, 2 / this.camera.zoom);
		ctx.strokeStyle = 'rgba(0, 255, 255, 0.5)';
		ctx.lineWidth = lineWidth;
		ctx.setLineDash([4, 4]);

		for (const bounds of this.spatialIndexBounds) {
			const screenPos = this.worldToScreen(bounds.x, bounds.y);
			const screenWidth = bounds.width * this.camera.zoom;
			const screenHeight = bounds.height * this.camera.zoom;

			ctx.strokeRect(screenPos.x, screenPos.y, screenWidth, screenHeight);
		}

		ctx.setLineDash([]);
	}

	private applyLODColors(): void {
		const nodeElements = this.container.querySelectorAll('.canvas-node');
		// Scale border width inversely with zoom so it's visible at all scales
		// At zoom 1, use 3px. At zoom 0.1, use 30px. At zoom 10, use 0.3px (min 2px)
		const borderWidth = Math.max(2, Math.min(30, 3 / this.camera.zoom));
		nodeElements.forEach((el) => {
			const element = el as HTMLElement;
			const lod = element.dataset.lod;
			if (lod && LOD_COLORS[lod]) {
				element.style.boxShadow = `inset 0 0 0 ${borderWidth}px ${LOD_COLORS[lod]}`;
			}
		});
	}

	private clearLODColors(): void {
		const nodeElements = this.container.querySelectorAll('.canvas-node');
		nodeElements.forEach((el) => {
			const element = el as HTMLElement;
			element.style.boxShadow = '';
		});
	}

	private applyNodeBounds(): void {
		const nodeElements = this.container.querySelectorAll('.canvas-node');
		// Scale outline width so it's visible at all zoom levels
		const outlineWidth = Math.max(1, Math.min(10, 1 / this.camera.zoom));
		nodeElements.forEach((el) => {
			const element = el as HTMLElement;
			element.style.outline = `${outlineWidth}px solid rgba(255, 165, 0, 0.7)`;
		});
	}

	private clearNodeBounds(): void {
		const nodeElements = this.container.querySelectorAll('.canvas-node');
		nodeElements.forEach((el) => {
			const element = el as HTMLElement;
			element.style.outline = '';
		});
	}

	// =========================================================================
	// Texture Preview
	// =========================================================================

	private refreshTexturePreviews(): void {
		if (!this.panel || !this.getTexturePreviews) return;

		const container = this.panel.querySelector('#debug-texture-previews');
		if (!container) return;

		const previews = this.getTexturePreviews();

		if (previews.length === 0) {
			container.innerHTML = '<div class="debug-texture-empty">No textures cached</div>';
			return;
		}

		container.innerHTML = previews.map(preview => {
			const sizeMB = (preview.memoryBytes / (1024 * 1024)).toFixed(2);
			return `
				<div class="debug-texture-item" title="${preview.nodeId}\n${preview.width}×${preview.height}\n${sizeMB} MB">
					<img src="${preview.dataUrl}" alt="${preview.nodeId}" />
					<div class="debug-texture-info">
						<span class="debug-texture-id">${preview.nodeId.slice(0, 8)}...</span>
						<span class="debug-texture-size">${preview.width}×${preview.height}</span>
					</div>
				</div>
			`;
		}).join('');
	}

	// =========================================================================
	// Coordinate Transforms
	// =========================================================================

	private worldToScreen(worldX: number, worldY: number): { x: number; y: number } {
		return {
			x: (worldX - this.camera.x) * this.camera.zoom + this.viewport.width / 2,
			y: (worldY - this.camera.y) * this.camera.zoom + this.viewport.height / 2
		};
	}
}

/**
 * CSS styles for the debug overlay.
 * These are injected when the overlay is first created.
 */
export const DEBUG_OVERLAY_STYLES = `
.debug-overlay-panel {
	position: absolute;
	top: 10px;
	right: 10px;
	width: 220px;
	background: rgba(0, 0, 0, 0.85);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 8px;
	color: #fff;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	font-size: 12px;
	z-index: 10000;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
	overflow: hidden;
}

.debug-panel-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 8px 12px;
	background: rgba(255, 255, 255, 0.1);
	border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.debug-panel-title {
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	font-size: 11px;
}

.debug-panel-close {
	background: none;
	border: none;
	color: #888;
	font-size: 18px;
	cursor: pointer;
	padding: 0 4px;
	line-height: 1;
}

.debug-panel-close:hover {
	color: #fff;
}

.debug-panel-content {
	padding: 8px 0;
	max-height: calc(100vh - 80px);
	overflow-y: auto;
}

.debug-section {
	padding: 8px 12px;
	border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.debug-section:last-child {
	border-bottom: none;
}

.debug-section-title {
	font-weight: 600;
	font-size: 10px;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #888;
	margin-bottom: 8px;
}

.debug-checkbox {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
	cursor: pointer;
}

.debug-checkbox input {
	margin: 0;
	cursor: pointer;
}

.debug-metrics {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.debug-metric {
	display: flex;
	justify-content: space-between;
	align-items: center;
}

.debug-metric-label {
	color: #888;
}

.debug-metric-value {
	font-family: 'Monaco', 'Menlo', monospace;
	color: #4ade80;
}

.debug-metric-value.warning {
	color: #facc15;
}

.debug-metric-value.critical {
	color: #ef4444;
}

.debug-lod-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
}

.debug-lod-item {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 11px;
}

.debug-lod-item::before {
	content: '';
	display: inline-block;
	width: 12px;
	height: 12px;
	background: var(--lod-color);
	border-radius: 2px;
}

/* Entity count visualization */
.debug-entity-summary {
	background: rgba(255, 255, 255, 0.05);
}

.debug-entity-bars {
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.debug-entity-bar {
	display: grid;
	grid-template-columns: 50px 32px 1fr;
	align-items: center;
	gap: 6px;
	height: 18px;
}

.debug-entity-label {
	font-size: 10px;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

.debug-entity-count {
	font-family: 'Monaco', 'Menlo', monospace;
	font-size: 11px;
	text-align: right;
}

.debug-entity-count.debug-entity-html {
	color: #22c55e;
}

.debug-entity-count.debug-entity-texture {
	color: #a855f7;
}

.debug-entity-count.debug-entity-svg {
	color: #3b82f6;
}

.debug-entity-bar-fill {
	height: 8px;
	border-radius: 4px;
	transition: width 0.2s ease;
	min-width: 2px;
}

.debug-entity-html-bar {
	background: linear-gradient(90deg, #22c55e, #16a34a);
}

.debug-entity-texture-bar {
	background: linear-gradient(90deg, #a855f7, #7c3aed);
}

.debug-entity-svg-bar {
	background: linear-gradient(90deg, #3b82f6, #2563eb);
}

/* Threshold controls */
.debug-threshold {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 4px 0;
}

.debug-threshold-label {
	font-size: 11px;
	color: #888;
}

.debug-threshold-input {
	width: 60px;
	padding: 4px 6px;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	color: #fff;
	font-size: 11px;
	font-family: 'Monaco', 'Menlo', monospace;
	text-align: right;
}

.debug-threshold-input:focus {
	outline: none;
	border-color: #3b82f6;
	background: rgba(255, 255, 255, 0.15);
}

.debug-threshold-input::-webkit-inner-spin-button,
.debug-threshold-input::-webkit-outer-spin-button {
	opacity: 1;
}

/* Texture Queue Debug Legend */
.debug-queue-legend {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 8px;
	padding-top: 8px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.debug-queue-item {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	font-size: 10px;
}

.debug-queue-item::before {
	content: '';
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 2px;
}

.debug-queue-pending::before {
	background: rgba(234, 179, 8, 0.9);
}

.debug-queue-processing::before {
	background: rgba(249, 115, 22, 0.9);
}

.debug-queue-skipped::before {
	background: rgba(239, 68, 68, 0.9);
}

.debug-queue-cached::before {
	background: rgba(168, 85, 247, 0.9);
}

/* Skip reasons */
.debug-skip-reasons {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 6px;
}

.debug-skip-reason {
	font-size: 9px;
	background: rgba(239, 68, 68, 0.2);
	color: #ef4444;
	padding: 2px 6px;
	border-radius: 4px;
}

.debug-texture-queue-section {
	background: rgba(234, 179, 8, 0.05);
}

/* Texture Preview */
.debug-texture-preview-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-top: 12px;
	margin-bottom: 8px;
	padding-top: 8px;
	border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.debug-section-subtitle {
	font-size: 10px;
	color: #888;
	text-transform: uppercase;
	letter-spacing: 0.03em;
}

.debug-refresh-btn {
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 4px;
	color: #888;
	font-size: 14px;
	cursor: pointer;
	padding: 2px 8px;
	line-height: 1;
	transition: all 0.15s ease;
}

.debug-refresh-btn:hover {
	background: rgba(255, 255, 255, 0.2);
	color: #fff;
}

.debug-texture-previews {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 6px;
	max-height: 200px;
	overflow-y: auto;
}

.debug-texture-empty {
	grid-column: 1 / -1;
	color: #666;
	font-size: 10px;
	text-align: center;
	padding: 16px;
}

.debug-texture-item {
	background: rgba(0, 0, 0, 0.3);
	border: 1px solid rgba(255, 255, 255, 0.1);
	border-radius: 4px;
	overflow: hidden;
	cursor: pointer;
	transition: border-color 0.15s ease;
}

.debug-texture-item:hover {
	border-color: rgba(168, 85, 247, 0.5);
}

.debug-texture-item img {
	width: 100%;
	height: 48px;
	object-fit: cover;
	display: block;
	background: rgba(255, 255, 255, 0.05);
}

.debug-texture-info {
	padding: 4px;
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.debug-texture-id {
	font-size: 8px;
	font-family: 'Monaco', 'Menlo', monospace;
	color: #a855f7;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.debug-texture-size {
	font-size: 8px;
	color: #666;
}

/* LOD Level Toggles */
.debug-lod-toggles {
	display: flex;
	gap: 8px;
	flex-wrap: wrap;
}

.debug-lod-toggle {
	display: flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	padding: 4px 8px;
	background: rgba(255, 255, 255, 0.05);
	border-radius: 4px;
	transition: background 0.15s ease;
}

.debug-lod-toggle:hover {
	background: rgba(255, 255, 255, 0.1);
}

.debug-lod-toggle input {
	margin: 0;
	cursor: pointer;
}

.debug-lod-toggle input:not(:checked) + .debug-lod-toggle-label {
	opacity: 0.4;
	text-decoration: line-through;
}

.debug-lod-toggle-label {
	font-size: 11px;
	display: flex;
	align-items: center;
	gap: 4px;
}

.debug-lod-toggle-label::before {
	content: '';
	display: inline-block;
	width: 8px;
	height: 8px;
	background: var(--lod-color);
	border-radius: 2px;
}

/* Force LOD controls */
.debug-force-lod {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-bottom: 8px;
}

.debug-radio {
	display: flex;
	align-items: center;
	gap: 4px;
	cursor: pointer;
	padding: 4px 8px;
	background: rgba(255, 255, 255, 0.05);
	border-radius: 4px;
	font-size: 11px;
	transition: background 0.15s ease;
}

.debug-radio:hover {
	background: rgba(255, 255, 255, 0.1);
}

.debug-radio input {
	margin: 0;
	cursor: pointer;
}

.debug-radio input:checked + span,
.debug-radio:has(input:checked) {
	color: #4ade80;
}
`;
